import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { getTargetValue } from "../../../shared/connectors/getTargetValue";
import {
  listFetchErrorKey,
  listFetchStateKey,
  listRowKey,
  listRows,
} from "../../../shared/connectors/listRows";
import type {
  FieldTarget,
  ListField,
  ListFieldColumn,
} from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { trySealSecret } from "../../sealSecret";
import { Box } from "../Box";
import { Button } from "../Button";
import { Input } from "../Input";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { App } from "./Form";

const Rows = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RemoveSpacer = styled("div")`
  width: 24px;
  flex-shrink: 0;
`;

const Actions = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

type Props = {
  item: ListField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
};

export function ListFieldItem({ item, app, disabled, onEdit }: Props) {
  const z = useZero();
  const persisted = listRows(app.configuration, item.prefix);
  const [draftIds, setDraftIds] = useState<number[]>([]);

  // Late debounce flushes from Input fire against stale closures; route every
  // write through the latest app state so they land in the current store.
  const appRef = useRef(app);
  useLayoutEffect(() => {
    appRef.current = app;
  });

  const persistedIds = new Set(persisted.map((row) => row.id));
  const ids = [
    ...persisted.map((row) => row.id),
    ...draftIds.filter((id) => !persistedIds.has(id)),
  ];

  const buildBase = () => ({
    connectedAppId: appRef.current.id,
    name: appRef.current.name,
    identifier: appRef.current.identifier,
    urlPattern: appRef.current.urlPattern,
    configuration: appRef.current.configuration,
  });

  const writeTarget = async (target: FieldTarget, value: string) => {
    if (target.kind === "secrets") {
      if (!value) return;
      const sealed = await trySealSecret(value);
      if (sealed === undefined) return;
      return z.mutate(
        sharedMutators.updateConnectedApp({
          ...buildBase(),
          secrets: { [target.field]: sealed },
        })
      );
    }
    if (target.kind !== "configuration") return;
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...buildBase(),
        configuration: {
          ...appRef.current.configuration,
          [target.field]: value,
        },
      })
    );
  };

  const isPlain = (key: string) =>
    appRef.current.configuration[key] !== undefined;

  const writeRowCell = (id: number, column: ListFieldColumn, value: string) => {
    const key = listRowKey(item.prefix, id, column.field);
    const kind = column.secret && !isPlain(key) ? "secrets" : "configuration";
    return writeTarget({ kind, field: key }, value);
  };

  const toggleSecret = async (
    id: number,
    column: ListFieldColumn,
    currentValue: string
  ) => {
    onEdit?.();
    const key = listRowKey(item.prefix, id, column.field);
    if (isPlain(key)) {
      const sealed = currentValue ? await trySealSecret(currentValue) : "";
      if (sealed === undefined) return;
      z.mutate(
        sharedMutators.updateConnectedApp({
          ...buildBase(),
          configuration: Object.fromEntries(
            Object.entries(appRef.current.configuration).filter(
              ([k]) => k !== key
            )
          ),
          secrets: { [key]: sealed },
        })
      );
      return;
    }
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...buildBase(),
        configuration: {
          ...appRef.current.configuration,
          [key]: currentValue,
        },
        secrets: { [key]: "" },
      })
    );
  };

  const removeRow = (id: number) => {
    onEdit?.();
    setDraftIds((prev) => prev.filter((draftId) => draftId !== id));
    if (!persistedIds.has(id)) return;
    const rowPrefix = `${item.prefix}.${id}.`;
    z.mutate(
      sharedMutators.updateConnectedApp({
        ...buildBase(),
        configuration: Object.fromEntries(
          Object.entries(appRef.current.configuration).filter(
            ([key]) => !key.startsWith(rowPrefix)
          )
        ),
        // Blank rather than orphan the secret: row ids can be reused, and a
        // stale value would silently revive under a future row.
        secrets: Object.fromEntries(
          item.columns
            .filter((column) => column.secret)
            .map((column) => [listRowKey(item.prefix, id, column.field), ""])
        ),
      })
    );
  };

  const addRow = () => {
    const nextId = ids.length ? Math.max(...ids) + 1 : 0;
    setDraftIds((prev) => [...prev, nextId]);
  };

  const fetchState = app.configuration[listFetchStateKey(item.prefix)];
  const fetching = fetchState === "fetching";
  const fetchError =
    fetchState === "error"
      ? app.configuration[listFetchErrorKey(item.prefix)]
      : null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <Rows>
        {item.primaryRow && (
          <Row>
            {item.columns.map((column) => {
              const cell = item.primaryRow![column.field];
              if (!cell) return null;
              const masked = cell.target.kind === "secrets";
              return (
                <Cell
                  key={column.field}
                  masked={masked}
                  value={getTargetValue(cell.target, app)}
                  showDots={masked && !app.fresh}
                  placeholder={cell.placeholder}
                  lock={masked ? "fixed" : "none"}
                  disabled={disabled}
                  onFinish={(value) => writeTarget(cell.target, value)}
                />
              );
            })}
            {!disabled && <RemoveSpacer />}
          </Row>
        )}
        {ids.map((id) => (
          <Row key={id}>
            {item.columns.map((column) => {
              const key = listRowKey(item.prefix, id, column.field);
              const secretNow =
                !!column.secret && app.configuration[key] === undefined;
              return (
                <Cell
                  key={column.field}
                  masked={secretNow}
                  value={app.configuration[key]}
                  showDots={secretNow && persistedIds.has(id) && !app.fresh}
                  placeholder={
                    secretNow
                      ? (column.secretPlaceholder ?? column.placeholder)
                      : column.placeholder
                  }
                  lock={column.secret ? "toggle" : "none"}
                  disabled={disabled}
                  onFinish={(value) => writeRowCell(id, column, value)}
                  onToggle={(currentValue) =>
                    toggleSecret(id, column, currentValue)
                  }
                />
              );
            })}
            {!disabled && (
              <Button
                type="button"
                variant="secondary"
                icon="x"
                onClick={() => removeRow(id)}
              />
            )}
          </Row>
        ))}
        {!disabled && (
          <Actions>
            <Button
              type="button"
              variant="secondary"
              icon="plus"
              onClick={addRow}
            >
              {item.addLabel}
            </Button>
            {item.fetch && (
              <Button
                type="button"
                variant="secondary"
                icon="refresh-cw"
                iconSpinning={fetching}
                onClick={() => {
                  onEdit?.();
                  z.mutate(
                    sharedMutators.refreshConnectorList({
                      connectedAppId: app.id,
                      prefix: item.prefix,
                    })
                  );
                }}
              >
                {fetching ? "Fetching…" : item.fetch.label}
              </Button>
            )}
          </Actions>
        )}
      </Rows>
      {fetchError && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-error)">
            {fetchError}
          </Text>
        </Box>
      )}
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
    </div>
  );
}

function Cell({
  masked,
  value,
  showDots,
  placeholder,
  lock,
  disabled,
  onFinish,
  onToggle,
}: {
  masked: boolean;
  value: string | undefined;
  showDots: boolean;
  placeholder?: string;
  lock: "none" | "fixed" | "toggle";
  disabled?: boolean;
  onFinish: (value: string) => void;
  onToggle?: (currentValue: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleToggle = () => {
    const input = inputRef.current;
    if (!input || !onToggle) return;
    // A stored-but-unread secret shows as the dots placeholder over an empty
    // value, so reading input.value carries the typed value or starts blank.
    onToggle(input.value);
  };

  return (
    <>
      <Input
        ref={inputRef}
        type="text"
        secret={masked}
        secretIsSet={showDots}
        defaultValue={value}
        placeholder={placeholder}
        style={{ flex: 1, minWidth: 0 }}
        onFinishChange={onFinish}
        readOnly={disabled}
      />
      {lock === "fixed" && (
        <Tooltip content="Encrypted secret">
          <Button
            type="button"
            variant="secondary"
            icon="lock-filled"
            disabled
          />
        </Tooltip>
      )}
      {lock === "toggle" && !disabled && (
        <Tooltip content={masked ? "Encrypted secret" : "Non-secret value"}>
          <Button
            type="button"
            variant="secondary"
            icon={masked ? "lock-filled" : "lock"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToggle}
          />
        </Tooltip>
      )}
    </>
  );
}
